import useIsFeatureEnabled from './useIsFeatureEnabled';
import { WeblabedFeatures } from 'src/commons/constants';
import {
  isUcbEverywherePath,
  isUcmEverywherePath,
} from 'src/commons/utils/a3cChatUrlRules';
import { isUnifiedA3CChatVetoed } from 'src/commons/utils/a3cChatGate';

/**
 * Whether the A3C everywhere experience is enabled for the CURRENT page,
 * selecting the weblab that actually controls this surface:
 *
 * - UCB everywhere paths (/cb campaign builders and /create/settings/st):
 *   A3C_EVERYWHERE_UCB_1459740 is the SOLE control — A3C_EVERYWHERE_1451089
 *   T1/T2 have no effect there.
 * - UCM everywhere paths (/cm and /campaign-manager):
 *   A3C_EVERYWHERE_UCM_1461968 is the SOLE control, same deal.
 * - Everywhere else: A3C_EVERYWHERE T1 or T2, as before.
 *
 * The three surfaces (UCB, UCM, rest of the console) launch on three
 * independent dials.
 *
 * On top of whichever dial applies, the AAO-owned unified gate
 * (`window.isA3CEnabled`, see isUnifiedA3CChatVetoed) can veto the everywhere
 * experience: an explicit `false` from Trim makes this hook return false
 * exactly as if the weblab were off. The gate is applied HERE — on the
 * everywhere input — rather than as a page-level chat veto, so it can only
 * withhold the everywhere default agent. Pages that wire their own (legacy)
 * agent through embeddedChatConfig.selectedAgentIdentifier have not migrated
 * onto AAO's flag and must keep their chat regardless of it.
 *
 * Single source of truth consumed by EmbeddedChatManager (panel + config
 * injection) and ConsolidatedSideNavInner (Ads Agent nav button) so the nav
 * button can never diverge from the panel on UCM pages — the sidenav DOES
 * render on /cm, so gating it on T1/T2 while the panel loads under the UCM
 * weblab would show a button without a chat (or vice versa).
 * useShowFocusHeaderAdsAgentToggle intentionally does NOT consume this: the
 * focus header exists only on UCB paths, where it checks the UCB weblab
 * directly (equivalent by construction on those paths) and applies the same
 * gate veto itself.
 *
 * Pathname is read at render time; URL rules are static for the lifetime of
 * a page load (matching every other consumer of a3cChatUrlRules).
 */
export default function useA3cEverywhereEnabled(): boolean {
  const isT1Enabled = useIsFeatureEnabled(WeblabedFeatures.A3C_EVERYWHERE);
  const isT2Enabled = useIsFeatureEnabled(WeblabedFeatures.A3C_EVERYWHERE_T2);
  const isUcbEnabled = useIsFeatureEnabled(WeblabedFeatures.A3C_EVERYWHERE_UCB);
  const isUcmEnabled = useIsFeatureEnabled(WeblabedFeatures.A3C_EVERYWHERE_UCM);

  // Hooks above run unconditionally; the veto short-circuits only the result.
  if (isUnifiedA3CChatVetoed()) {
    return false;
  }
  if (isUcbEverywherePath(window.location.pathname)) {
    return isUcbEnabled;
  }
  if (isUcmEverywherePath(window.location.pathname)) {
    return isUcmEnabled;
  }
  return isT1Enabled || isT2Enabled;
}
